"use client";

import { motion } from "framer-motion";
import { Minus, Plus, ShoppingCart } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useId, useState } from "react";
import {
  NutritionalTables,
  ProductDescription,
} from "@/components/product/nutritional-information";
import { SocialShare } from "@/components/product/social-share";
import { TrustBadges } from "@/components/product/trust-badges";
import { WishlistButton } from "@/components/product/wishlist-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCartEnabled } from "@/hooks/use-feature-flags";
import type { ValidProduct } from "@/hooks/use-product";
import { mapVendorToBrand } from "@/hooks/use-shop-filters";
import { getBaseUrl } from "@/lib/utils/environment";

type ProductDetailProps = {
  product: ValidProduct;
  onAddToCart?: (
    productId: string,
    variantId: string,
    quantity: number
  ) => void;
};

/**
 * Production-ready ProductDetail component following UI/UX specifications
 * Implements clean, minimalist design with systematic spacing and typography
 */
export function ProductDetail({ product, onAddToCart }: ProductDetailProps) {
  // compute flavor option from product first, then initialise state from it
  const flavorOption = product.options.find(
    (option) =>
      option.name.toLowerCase() === "flavor" ||
      option.name.toLowerCase() === "flavour"
  );
  const MAX_FLAVORS = 4; // threshold to switch to select input
  const [selectedFlavor, setSelectedFlavor] = useState<string>(() => {
    return flavorOption?.values?.[0] ?? "";
  });
  const [quantity, setQuantity] = useState(1);
  const isCartEnabled = useCartEnabled();
  const flavorId = useId();
  const quantityId = useId();

  const primaryImage = product.images[0];
  const price = Number.parseFloat(product.priceRange.minVariantPrice.amount);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  // Get the selected variant based on flavor
  const selectedVariant =
    product.variants.find((variant) =>
      variant.selectedOptions.some(
        (option) =>
          option.name.toLowerCase().includes("flavor") &&
          option.value === selectedFlavor
      )
    ) || product.variants[0];

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart(product.id, selectedVariant.id, quantity);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-8">
      <div className="grid gap-16 lg:grid-cols-2">
        {/* Left Column: Product Image */}
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="relative"
          initial={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative aspect-square overflow-hidden rounded-xl border border-gray-200 bg-white">
            <Image
              alt={primaryImage?.altText || product.title}
              className="object-cover"
              fill
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
              src={primaryImage?.url || "/placeholder-product.jpg"}
            />
          </div>
        </motion.div>

        {/* Right Column: Product Information */}
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
          initial={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="text-gray-600 text-sm">
            <Link
              className="transition-colors hover:text-gray-900"
              href="/shop"
            >
              Shop
            </Link>
            <span className="mx-2">•</span>
            <span className="text-gray-400">
              {product.productType || "General"}
            </span>
            <span className="mx-2">•</span>
            <span className="text-gray-900">{product.vendor}</span>
          </nav>

          {/* Product Title */}
          <div className="space-y-2">
            <h1 className="font-bold font-heading text-4xl text-gray-900 tracking-tight">
              {product.title}
            </h1>

            {/* Brand Line */}
            <Link
              className="text-gray-600 text-lg transition-colors hover:text-gray-900 hover:underline"
              href={`/shop?brands=${mapVendorToBrand(product.vendor)}`}
            >
              By {product.vendor}
            </Link>
          </div>

          {/* Price */}
          <div className="font-bold text-3xl text-gray-900">
            £{price.toFixed(2)}
          </div>

          {/* Social Share - Mobile only (visible below price on mobile) */}
          {/* Utilities row (Share + future utility actions) */}
          <div className="flex flex-col items-start gap-4 border-t border-b py-4 sm:flex-row sm:items-center">
            <div className="flex items-center justify-start gap-4">
              <SocialShare
                compact={false}
                productTitle={product.title}
                productUrl={`${getBaseUrl()}/products/${product.handle}`}
                showHeading={false}
              />
            </div>
          </div>

          {/* Product Description */}
          <ProductDescription product={product} />

          {/* Flavor Selector */}
          {flavorOption && (
            <div className="space-y-4">
              <Label
                className="font-medium text-base text-gray-900"
                htmlFor={flavorId}
              >
                Flavor:
              </Label>
              {flavorOption.values.length > MAX_FLAVORS ? (
                <Select
                  onValueChange={setSelectedFlavor}
                  value={selectedFlavor}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a flavor" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...flavorOption.values]
                      .sort((a, b) => a.localeCompare(b))
                      .map((flavor) => (
                        <SelectItem key={flavor} value={flavor}>
                          {flavor}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  aria-labelledby={flavorId}
                  className="flex flex-wrap gap-2"
                  role="radiogroup"
                >
                  {[...flavorOption.values]
                    .sort((a, b) => a.localeCompare(b))
                    .map((flavor) => (
                      <motion.button
                        aria-checked={selectedFlavor === flavor}
                        aria-label={`Select ${flavor} flavor`}
                        className={`rounded-md border px-3 py-1.5 text-sm transition-all duration-200 ${
                          selectedFlavor === flavor
                            ? "border-red-600 bg-red-600 text-white"
                            : "border-gray-300 bg-white text-gray-900 hover:border-gray-400"
                        }`}
                        key={flavor}
                        onClick={() => setSelectedFlavor(flavor)}
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {flavor}
                      </motion.button>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Quantity Selector - only show when commerce (cart) is enabled */}
          {isCartEnabled && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-4">
                  <Label
                    className="font-medium text-base text-gray-900"
                    htmlFor={quantityId}
                  >
                    Quantity:
                  </Label>
                  <div className="flex items-center space-x-3">
                    <Button
                      aria-label="Decrease quantity"
                      disabled={quantity <= 1}
                      onClick={() => handleQuantityChange(quantity - 1)}
                      size="icon"
                      variant="outline"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      aria-label="Product quantity"
                      className="w-20 text-center"
                      id={quantityId}
                      max="10"
                      min="1"
                      onChange={(e) =>
                        handleQuantityChange(
                          Number.parseInt(e.target.value, 10)
                        )
                      }
                      type="number"
                      value={quantity}
                    />
                    <Button
                      aria-label="Increase quantity"
                      disabled={quantity >= 10}
                      onClick={() => handleQuantityChange(quantity + 1)}
                      size="icon"
                      variant="outline"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* (Utilities consolidated above) */}
              </div>
            </div>
          )}

          {/* (Utilities consolidated above) */}

          {/* Action Buttons - Only show when commerce is enabled */}
          {isCartEnabled && (
            <div className="flex items-center space-x-4">
              <Button
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                disabled={!product.availableForSale}
                onClick={handleAddToCart}
                size="lg"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Add to Basket
              </Button>

              <WishlistButton
                productId={product.id}
                productName={product.title}
                showText={false}
                size="lg"
                variant="ghost"
                variantId={selectedVariant.id}
              />
            </div>
          )}

          {/* Trust Signals */}
          <div className="pt-4">
            <TrustBadges />
          </div>
        </motion.div>
      </div>

      {/* Nutritional Information - Full width below product details */}
      <div className="mt-16">
        <NutritionalTables product={product} />
      </div>
    </main>
  );
}
